/** Owns the `ReportSpec` being created, edited or viewed, and the grid it renders to. */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createReport,
  getReport,
  importScreen,
  previewReport,
  queryReport,
  updateReport,
  explainReport
} from 'src/components/star-grid/api/reportEngineClient';
import type { QueryResponse, ReportSpec, ReportQueryRequest } from 'src/components/star-grid/api/reportEngineTypes';
import { isSpecComplete, isSpecPreviewable, savedReportQuery, specFromTemplate } from 'src/components/star-grid/utils/reportSpec';
import { withRowPresentation } from 'src/components/star-grid/services/savedReportPresentation';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

export interface ReportSpecController {
  readonly spec: ReportSpec | undefined;
  readonly setSpec: (next: ReportSpec) => void;
  /**
   * Seeds the spec from one deck screen. Every starting point routes through here because only a screen
   * carries the pre-resolved member list a renderable spec needs. Resolves to the screen that lent the
   * rows axis when it was borrowed, so the caller can say so.
   */
  readonly startFromScreen: (
    screenId: string,
    name: string,
    origin?: { readonly template?: string; readonly template_label?: string }
  ) => Promise<string | null>;
  /**
   * Seeds a NEW report from a saved report's arrangement. The `id` is dropped so the next save cannot
   * PUT over the source, and the name is dropped because live report names are unique.
   */
  readonly startFromReport: (reportId: string) => Promise<void>;
  readonly grid: QueryResponse | undefined;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly running: boolean;
  readonly error: string | undefined;
  readonly canSave: boolean;
  /** A preview needs a cube and a row axis, not a name: a new report starts unnamed. */
  readonly canPreview: boolean;
  /** POST without an id, PUT with one; `overrides` carries what the studio holds outside the spec. */
  readonly save: (overrides?: Partial<ReportSpec>) => Promise<ReportSpec | undefined>;
  /** Drops the grid and any run still in flight, so nothing stale stays on screen. */
  readonly clear: () => void;
  /**
   * Queries a saved spec, previews an unsaved one; `specOverride` previews the studio's shelves, and
   * `started` is an already-running query for the same view to use instead of sending another.
   */
  readonly run: (request?: Partial<ReportQueryRequest>, specOverride?: ReportSpec, started?: Promise<QueryResponse>) => Promise<void>;
  /** The default-view query sent alongside the spec read, once; undefined after it is taken. */
  readonly takeOpeningQuery: () => Promise<QueryResponse> | undefined;
  /** StarRocks' plans for the last saved-report query, planned again without running it. */
  readonly explain: () => Promise<readonly string[]>;
}

function message(err: unknown): string {
  return errorMessage(err);
}

function useLoadedSpec(
  reportId: string | undefined,
  setSpec: (spec: ReportSpec) => void,
  setError: (error: string | undefined) => void,
  presentation: boolean
) {
  const [loading, setLoading] = useState(Boolean(reportId));
  useEffect(() => {
    if (!reportId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    getReport(reportId, controller.signal)
      // Enriched BEFORE the spec is published, so the shelves and the line set are projected once,
      // already carrying the row presentation.
      .then((loaded) => (presentation ? withRowPresentation(loaded, controller.signal) : loaded))
      .then((loaded) => {
        setSpec(loaded);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(message(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reportId, setSpec, setError, presentation]);
  return loading;
}

/**
 * @param reportId load an existing report (edit/view); omit to start a create flow.
 * @param presentation enrich a loaded spec with the deck's row presentation, which only the editor reads.
 */
export function useReportSpec(reportId?: string, presentation = true, prefetchQuery = false): ReportSpecController {
  // Sent with the spec read rather than after it: on open every knob is the report's own default, which
  // the server resolves from the stored spec.
  const opening = useRef<Promise<QueryResponse> | null>();
  if (prefetchQuery && reportId && opening.current === undefined) {
    opening.current = queryReport(reportId, savedReportQuery({}));
    // Unused when the first view is not the default one; its failure is then nobody's to report.
    opening.current.catch(() => undefined);
  }
  const takeOpeningQuery = useCallback(() => {
    const started = opening.current ?? undefined;
    opening.current = null;
    return started;
  }, []);
  const [spec, setSpec] = useState<ReportSpec | undefined>(undefined);
  const [grid, setGrid] = useState<QueryResponse | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const loading = useLoadedSpec(reportId, setSpec, setError, presentation);

  const seed = useCallback((seeded: ReportSpec) => {
    setSpec(seeded);
    setGrid(undefined);
  }, []);

  const startFromScreen = useCallback(
    async (screenId: string, name: string, origin?: { readonly template?: string; readonly template_label?: string }): Promise<string | null> => {
      setError(undefined);
      try {
        const imported = await importScreen(screenId);
        seed({ ...specFromTemplate(imported, name || imported.name), ...origin });
        return imported.rows_borrowed_from;
      } catch (err: unknown) {
        setError(message(err));
        return null;
      }
    },
    [seed]
  );

  const startFromReport = useCallback(
    async (sourceId: string): Promise<void> => {
      setError(undefined);
      try {
        const { id: _ignored, ...arrangement } = await getReport(sourceId);
        seed(await withRowPresentation({ ...arrangement, name: '' } as ReportSpec));
      } catch (err: unknown) {
        setError(message(err));
      }
    },
    [seed]
  );

  const save = useCallback(
    async (overrides: Partial<ReportSpec> = {}): Promise<ReportSpec | undefined> => {
      if (!spec) return undefined;
      const sending = { ...spec, ...overrides };
      setSaving(true);
      setError(undefined);
      try {
        // PUT replaces, so the spec is sent whole either way. A row the engine did not author answers 409.
        const stored = sending.id ? await updateReport(sending.id, sending) : await createReport(sending);
        setSpec(stored);
        return stored;
      } catch (err: unknown) {
        setError(message(err));
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [spec]
  );

  // Only the newest run may set the grid, so a slow answer to an older edit cannot overwrite a newer one.
  const latestRun = useRef(0);
  const lastQuery = useRef<{ readonly id: string; readonly request: ReportQueryRequest }>();
  const run = useCallback(
    async (request: Partial<ReportQueryRequest> = {}, specOverride?: ReportSpec, started?: Promise<QueryResponse>): Promise<void> => {
      const target = specOverride ?? spec;
      if (!target) return;
      const runId = ++latestRun.current;
      const current = () => runId === latestRun.current;
      setRunning(true);
      setError(undefined);
      try {
        // The studio's shelves are unsaved edits, so they are previewed even on a saved report.
        const saved = target.id && !specOverride;
        const body = savedReportQuery(request);
        if (saved) lastQuery.current = { id: target.id!, request: body };
        const grid = saved ? await (started ?? queryReport(target.id!, body)) : await previewReport({ spec: target, ...request });
        if (current()) setGrid(grid);
      } catch (err: unknown) {
        if (current()) setError(message(err));
      } finally {
        if (current()) setRunning(false);
      }
    },
    [spec]
  );

  const explain = useCallback(
    (): Promise<readonly string[]> => (lastQuery.current ? explainReport(lastQuery.current.id, lastQuery.current.request) : Promise.resolve([])),
    []
  );

  const clear = useCallback(() => {
    latestRun.current += 1;
    setGrid(undefined);
    setRunning(false);
  }, []);

  return {
    spec,
    setSpec,
    clear,
    takeOpeningQuery,
    explain,
    startFromScreen,
    startFromReport,
    grid,
    loading,
    saving,
    running,
    error,
    canSave: Boolean(spec && isSpecComplete(spec)),
    canPreview: Boolean(spec && isSpecPreviewable(spec)),
    save,
    run
  };
}
